import { Pagination, Spinner, Table as TableComponent, TableProps } from '@/components/Elements';
import { ROLES, useAuthorization } from '@/lib/authorization';

import { useUsers } from '../../api/getUsers';
import { DeleteUser } from '../DeleteUser';
import { UpdateUserForm } from '../UpdateUserForm';

type TUser = {
  avatar: string;
  id: number;
  firstName: string;
  lastName: string;
  role?: string;
  isOwner: boolean;
  email: string;
};

const tableColumns: TableProps<TUser>['columns'] = [
  {
    title: 'full name',
    field: 'firstName',
    Cell: ({ entry: { avatar, firstName, lastName } }) => (
      <div className="flex items-center gap-x-2">
        <img className="mr-1 h-8 w-8 rounded-full" src={avatar} alt="user avatar" />
        <span className="font-medium text-ebony-clay-900">
          {firstName} {lastName}
        </span>
      </div>
    ),
  },
  {
    title: 'Email',
    field: 'email',
    isSortable: false,
  },
  {
    title: 'Role',
    field: 'role',
    isSortable: false,
    Cell: ({ entry: { role } }) => <span className="capitalize">{role}</span>,
  },
];

export const Table = () => {
  const { checkAccess } = useAuthorization();

  const usersQuery = useUsers({
    config: {
      keepPreviousData: true,
    },
  });

  if (usersQuery.isLoading) {
    return (
      <div className="flex h-48 w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!usersQuery.data) return null;

  const { data, links, meta } = usersQuery.data;

  const users: TUser[] = data.map((user) => ({
    avatar: user.avatar,
    firstName: user.firstName,
    lastName: user.lastName,
    id: user.id,
    role: user.role,
    email: user.email,
    isOwner: user.isOwner,
  }));

  const columns = [
    ...tableColumns,
    ...(checkAccess({ allowedRoles: [ROLES.superAdmin, ROLES.admin] })
      ? [
          {
            title: '',
            field: 'id',
            isNarrowColumn: true,
            isSortable: false,
            Cell: ({
              entry: { id, isOwner, email, firstName, lastName, role },
            }: {
              entry: TUser;
            }) =>
              !isOwner ? (
                <div className="flex items-center gap-x-6">
                  <UpdateUserForm
                    defaultValues={{
                      email,
                      fullName: `${firstName} ${lastName}`,
                      role: role || '',
                    }}
                    id={id}
                  />
                  <DeleteUser id={id} name={`${firstName} ${lastName}`} />
                </div>
              ) : null,
          },
        ]
      : []),
  ].filter(Boolean) as TableProps<TUser>['columns'];

  return (
    <TableComponent<TUser>
      data={users}
      emptyState="No users found"
      isFetching={usersQuery.isFetching && usersQuery.isPreviousData}
      columns={columns}
      pagination={
        <Pagination
          links={links}
          meta={meta}
          path="/users"
          isFetching={usersQuery.isFetching && usersQuery.isPreviousData}
        />
      }
    />
  );
};
